import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { ExpandableText } from '@components/expandable-text';
import { createForm, createFormStore } from '@components/form';
import { FormStoreInstance } from '@components/form/create-form-store';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { generateTemplateName, useIntegrationEndpoint } from '@components/integrations';
import {
  DefaultFileType,
  IntegrationEndpointVariable,
} from '@components/integrations/integrations-types';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { forceHideSidebar, Sidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';

import { useMarketplaceTemplate, useTemplateEndpoint } from 'api/app-marketplace/hooks';
import { createJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { TimeSavedContainer } from 'ui/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { AppNotConnectedSection, DisabledSection, SidebarWarningSection } from './common';

import { TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';
import { extractRequestHeaders } from '../shared/field-utils';

type IntegrationData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  integrationEndpointId: string;
  continueOnError: boolean;
} & Record<string, { value: string; variables: object } | string> &
  TimeSavedData;

const ACTION_TYPE = ACTION_TYPES.integration;
const Form = createForm<IntegrationData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const [isTemplate, setIsTemplate] = useState(false);
  const templateEndpointId = data?.values?.templateEndpointId;
  const integrationEndpointId = data?.values?.integrationEndpointId?.value;
  useEffect(() => {
    if (templateEndpointId && !integrationEndpointId) {
      setIsTemplate(true);
    } else if (integrationEndpointId) {
      setIsTemplate(false);
    }
  }, [templateEndpointId, integrationEndpointId]);

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }
  return isTemplate && templateEndpointId ? (
    <TemplateSidebar templateEndpointId={templateEndpointId} {...props} />
  ) : (
    <EndpointSidebar {...props} />
  );
};

const EndpointSidebar = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const values = data?.values;
  const { t } = useScopedTranslate(sidebarLocale);
  const {
    data: endpointData,
    isLoading: endpointIsLoading,
    isError: endpointIsError,
  } = useIntegrationEndpoint(
    values?.integrationEndpointId.value ?? '',
    // Very hacky way to make sure that when you edit the variable default value in the integration builder and create a new action that it doesn't use the old default value.
    {
      queryKeyHashFn: (queryKey: string[]) => {
        return queryKey + journeyActionId;
      },
      cacheTime: 0,
    }
  );
  const templateEndpointId = endpointData?.templateEndpointId;
  // If this is a template endpoint, fetch the template to check connection status
  const { data: templateEndpointData } = useTemplateEndpoint(templateEndpointId ?? '');
  const { data: template, isLoading: templateLoading } = useMarketplaceTemplate(
    templateEndpointId ? templateEndpointData?.templateId : undefined
  );
  const hasAuthError = Boolean(template && template?.connectionStatus !== 'connected');
  const variables = endpointData?.variables ?? [];
  const requestHeaders = (endpointData?.requestHeaders ?? []).filter((h) => h.visible);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyAction(journeyActionId, { onSuccess: () => false });
  const uploadMutation = useUploadFile(
    createJourneyActionDocumentPath(data?.journey ?? '', journeyActionId),
    true
  );
  const fieldMappingId = getFieldMappingId(data?.journey ?? '');

  const extractRequestData = (data: IntegrationData) => {
    const prefix = 'requestData__';
    const requestData: { [key: string]: any } = {};

    const setArrayValues = (obj: any, path: (string | number)[], value: any) => {
      let current = obj;
      for (let i = 0; i < path.length - 1; i++) {
        const part = path[i];
        if (!current[part]) {
          if (isNaN(Number(path[i + 1]))) {
            current[part] = {};
          } else {
            current[part] = [];
          }
        }
        current = current[part];
      }
      current[path[path.length - 1]] = value;
    };

    Object.keys(data).forEach((key) => {
      if (key.startsWith(prefix)) {
        const newKey = key.substring(prefix.length);
        const value = data[key];
        if (!value) return;
        let processedValue = value;
        if (typeof value !== 'object') {
          processedValue = { value, variables: {} };
        }
        const variable = variables.find((v) => v.name === newKey);
        if (variable) {
          requestData[newKey] = processedValue;
        } else {
          const path = newKey
            .split('__')
            .map((part) => (isNaN(Number(part)) ? part : Number(part)));
          setArrayValues(requestData, path, processedValue);
        }
      }
    });

    return requestData;
  };

  const handleSave = async (data: IntegrationData, shouldSkipClosing?: boolean) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: ACTION_TYPE,
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        integrationEndpointId: values?.integrationEndpointId,
        requestData: extractRequestData(data),
        requestHeaders: extractRequestHeaders(data),
        continueOnError: data.continueOnError,
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });
    !shouldSkipClosing && forceHideSidebar();
  };

  const variableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
  });

  const emailVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['email'],
  });

  const dateVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['date'],
  });

  const optionsVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['select'],
  });

  const fileVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['file'],
  });

  const getVariableGroups = (fieldType: FieldType) => {
    const typeGroupMap: Partial<Record<FieldType, FieldMappingGroup[]>> = {
      email: emailVariableGroups,
      date: dateVariableGroups,
      select: optionsVariableGroups,
      file: fileVariableGroups,
    };

    return typeGroupMap[fieldType] ?? variableGroups;
  };

  // Hack start - Even worse hacky method to save the integration  if it hasn't been saved before and has default values
  const noValuesSaved =
    !endpointIsLoading &&
    (!data?.values || Object.keys(data.values.requestData ?? {}).length === 0);
  const hasDefaultValues =
    variables.filter((variable) => Boolean(variable.default_value)).length > 0 ||
    (endpointData?.defaultFiles ?? []).length > 0;

  const needsToSaveDefaultValues = noValuesSaved && hasDefaultValues;

  useEffect(() => {
    if (needsToSaveDefaultValues) {
      const formData = formStore.getState().values as IntegrationData;
      handleSave(formData, true);
    }
    // eslint-disable-next-line
  }, [needsToSaveDefaultValues]);
  // Hack end

  if (isLoading || !data || endpointIsLoading || (templateEndpointId && templateLoading)) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  const Container = hasAuthError ? DisabledSection : Fragment;

  return (
    <Sidebar>
      <ActionHeader
        actionType={ACTION_TYPE}
        title={endpointData?.name}
        customIcon={
          endpointData?.thumbnail ? <SquareImage src={endpointData.thumbnail} /> : undefined
        }
        subtitle={endpointData?.integrationName}
        subtitleFont="regular12"
        subtitleLocation="below"
        {...props}
      />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
          {endpointIsError ? (
            <InfoBox variant="red" icon={<IconFormIndicatorError />} text={t('integrationError')} />
          ) : (
            <Stack orientation="vertical" space={6}>
              {hasAuthError && (
                <SidebarWarningSection noMargin>
                  <AppNotConnectedSection
                    template={template}
                    journeyActionId={props.journeyActionId}
                  />
                </SidebarWarningSection>
              )}
              <Container>
                <HideContainer title={t('actionDetails')}>
                  <Stack space={4}>
                    <ExpandableText text={endpointData?.description} />
                    <DescriptionFormField defaultValue={data.description} />
                  </Stack>
                </HideContainer>
                <Sidebar.Separator />
                <HideContainer title={t('triggerMethod')}>
                  <ExecutionMethod
                    assignees={data.assignees}
                    isAutomatic={data.isAutomatic}
                    formStore={formStore}
                    hideLabel
                  />
                </HideContainer>
                {variables.length > 0 && (
                  <>
                    <Sidebar.Separator />
                    <HideContainer title={t('requestValues')}>
                      <Stack space={4}>
                        {endpointIsLoading && <Spinner />}
                        {variables.map((field) => (
                          <IntegrationField
                            key={field.name}
                            field={field}
                            allFields={variables}
                            defaultValue={values?.requestData?.[field.name]}
                            variableGroups={getVariableGroups(field.type)}
                            uploadMutation={uploadMutation}
                            defaultFiles={endpointData?.defaultFiles}
                            isNewAction={noValuesSaved}
                            fieldMappingId={fieldMappingId}
                            formStore={formStore}
                          />
                        ))}
                      </Stack>
                    </HideContainer>
                  </>
                )}
                {requestHeaders.length > 0 && (
                  <>
                    <Sidebar.Separator />
                    <Text font="bold14" text={t('requestHeaders')} />
                    {requestHeaders.map((header) => (
                      <Form.FieldMappingTextEditor
                        key={header.name}
                        id={`requestHeaders__${header.name}`}
                        label={header.label}
                        defaultValue={
                          values?.requestHeaders?.[header.name] ??
                          (header.default_value
                            ? ({ value: header.default_value } as VariableValue)
                            : undefined)
                        }
                        variableGroups={variableGroups}
                        singleLine
                        plaintext
                        fieldMappingId={fieldMappingId}
                      />
                    ))}
                  </>
                )}
              </Container>
            </Stack>
          )}
          <Sidebar.Separator />
          <Container>
            <HideContainer title={t('advanced')} defaultClosed>
              <Form.Checkbox
                // @ts-ignore
                id="continueOnError"
                label={t('continueOnError')}
                defaultValue={values?.continueOnError ?? false}
              />
            </HideContainer>
            <Sidebar.Separator />
            <TimeSavedContainer {...data.timeSaved} />
          </Container>
          <ActionFooter
            action={data}
            isDisabled={!isDirty || editMutation.isLoading || hasAuthError}
            form={Form}
          />
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const TemplateSidebar = ({
  templateEndpointId,
  ...props
}: SidebarProps & { templateEndpointId: string }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { data: endpointData, isLoading: endpointLoading } =
    useTemplateEndpoint(templateEndpointId);
  const { data: template, isLoading: templateLoading } = useMarketplaceTemplate(
    endpointData?.templateId
  );
  const variables = endpointData?.variables ?? [];

  if (endpointLoading || !endpointData || templateLoading) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader
        actionType={ACTION_TYPE}
        title={endpointData?.name}
        subtitle={endpointData?.integrationName}
        subtitleFont="regular12"
        subtitleLocation="below"
        customIcon={
          endpointData?.thumbnail ? <SquareImage src={endpointData.thumbnail} /> : undefined
        }
        {...props}
      />
      <Sidebar.Content>
        <TemplatePreviewForm onSubmit={() => {}}>
          <Stack space={6}>
            <AppNotConnectedSection template={template} journeyActionId={props.journeyActionId} />
            <Sidebar.Separator />
            <DisabledSection>
              <HideContainer title={t('actionDetails')}>
                <Stack space={4}>
                  <ExpandableText text={endpointData?.description} />
                  <DescriptionFormField defaultValue="" />
                </Stack>
              </HideContainer>
              <Sidebar.Separator />
              <HideContainer title={t('triggerMethod')}>
                <ExecutionMethod
                  assignees={[]}
                  isAutomatic
                  formStore={createFormStore()}
                  hideLabel
                />
              </HideContainer>
              {variables.length > 0 && (
                <>
                  <Sidebar.Separator />
                  <HideContainer title={t('requestValues')}>
                    <Stack space={4}>
                      {variables.map((field) => (
                        <IntegrationField
                          key={field.name}
                          field={field}
                          allFields={variables}
                          defaultValue={undefined}
                          variableGroups={[]}
                          uploadMutation={null as unknown as ReturnType<typeof useUploadFile>}
                          defaultFiles={undefined}
                          isNewAction={false}
                          fieldMappingId=""
                        />
                      ))}
                    </Stack>
                  </HideContainer>
                </>
              )}
            </DisabledSection>
          </Stack>
          <ActionFooter
            action={{
              shortId: props.journeyActionId,
              action: ACTION_TYPE,
              description: undefined,
              isConfigured: false,
            }}
            isDisabled={true}
            form={TemplatePreviewForm}
          />
        </TemplatePreviewForm>
      </Sidebar.Content>
    </Sidebar>
  );
};

const TemplatePreviewForm = createForm<Record<string, unknown>>();

const sidebarLocale = {
  en: {
    integrationError:
      'Failed to load integration, if the integration has been deleted please remove the action',
    addMore: 'Add more',
    advanced: 'Advanced',
    continueOnError: 'Continue on error',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    requestValues: 'Request values',
    oneItemPerLine: 'One item per line',
    requestHeaders: 'Request Headers',
  },
};

type IntegrationFieldProps = {
  field: IntegrationEndpointVariable;
  allFields: IntegrationEndpointVariable[];
  defaultValue?: VariableValue | { [key: string]: VariableValue }[];
  variableGroups?: FieldMappingGroup[];
  uploadMutation?: ReturnType<typeof useUploadFile>;
  prefix?: string;
  defaultFiles?: DefaultFileType[];
  isNewAction?: boolean;
  fieldMappingId?: string;
  formStore?: FormStoreInstance;
};

export const IntegrationField = ({
  field,
  allFields,
  defaultValue: propDefaultValue,
  variableGroups = [],
  uploadMutation,
  prefix = 'requestData',
  defaultFiles,
  isNewAction = false,
  fieldMappingId = '',
  formStore,
}: IntegrationFieldProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const variableName = generateTemplateName(allFields, field);

  const getFieldDefault = useCallback(() => {
    let fieldDefault = undefined;
    if (isNewAction) {
      if (field.type === 'file') {
        const defaultFile = (defaultFiles ?? []).find((file) => file.variableName === variableName);
        if (defaultFile) {
          fieldDefault = {
            value: defaultFile.src,
          } as VariableValue;
        }
      } else if (field.default_value) {
        fieldDefault = { value: field.default_value } as VariableValue;
      }
    }
    return fieldDefault;
  }, [defaultFiles, field, isNewAction, variableName]);

  const [defaultValue, setDefaultValue] = useState<
    VariableValue | { [key: string]: VariableValue }[] | undefined
  >(propDefaultValue ?? getFieldDefault());

  useEffect(() => {
    if (!propDefaultValue && field.type === 'array') {
      setDefaultValue([{}]);
    } else {
      setDefaultValue(propDefaultValue ?? getFieldDefault());
    }
  }, [propDefaultValue, field, getFieldDefault]);

  const arrayCountId = `__arrayMeta__${prefix}__${field.name}__count`;
  const initialArrayCount = useRef(
    Array.isArray(propDefaultValue) ? propDefaultValue.length : field.type === 'array' ? 1 : 0
  );

  useEffect(() => {
    if (field.type === 'array' && formStore) {
      formStore.actions.register({
        id: arrayCountId,
        defaultValue: initialArrayCount.current,
      });
      return () => formStore.actions.unregister(arrayCountId);
    }
    // eslint-disable-next-line
  }, []);

  const handleAddMoreClick = () => {
    if (Array.isArray(defaultValue)) {
      const newValue = [...defaultValue, {}];
      setDefaultValue(newValue);
      formStore?.actions.setValue(arrayCountId, newValue.length);
    } else {
      setDefaultValue([{}]);
      formStore?.actions.setValue(arrayCountId, 1);
    }
  };

  const handleRemoveClick = (index: number) => {
    if (Array.isArray(defaultValue)) {
      const updatedValue = [...defaultValue];
      updatedValue.splice(index, 1);
      setDefaultValue(updatedValue);
      formStore?.actions.setValue(arrayCountId, updatedValue.length);
    }
  };
  const defaultId = `${prefix}__${field.name}`;

  const templates = field.template ?? [];

  const renderFieldContent = () => {
    switch (field.type) {
      case 'number':
        return (
          <Form.FieldMappingTextEditor
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            plaintext
            singleLine
            fieldMappingId={fieldMappingId}
          />
        );
      case 'select':
        return (
          <Form.FieldMappingSelectField
            id={defaultId}
            label={field.label}
            options={field.options ?? []}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
          />
        );
      case 'date':
        return (
          <Form.FieldMappingDatePicker
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
          />
        );
      case 'email':
        return (
          <Form.EmailField
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
          />
        );
      case 'file':
        return (
          <Form.FieldMappingFileField
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            onSelect={uploadMutation?.mutateAsync}
            fieldMappingId={fieldMappingId}
          />
        );
      case 'boolean':
        return (
          <Form.Switch
            // @ts-ignore What is this again? Why is id giving me an error?
            id={`${defaultId}`}
            label={field.label}
            defaultValue={Boolean((defaultValue as VariableValue)?.value) ?? false}
          />
        );
      case 'array_of_strings': {
        const rawValue = (defaultValue as VariableValue)?.value;
        const editorDefault: VariableValue = {
          value: Array.isArray(rawValue) ? rawValue.join('\n') : (rawValue as string) ?? '',
          variables: (defaultValue as VariableValue)?.variables ?? {},
        };
        return (
          <Stack space={1}>
            <Form.FieldMappingTextEditor
              id={defaultId}
              plaintext
              label={field.label}
              defaultValue={editorDefault}
              variableGroups={variableGroups}
              fieldMappingId={fieldMappingId}
            />
            <Text font="regular14" color="textHelper" text={t('oneItemPerLine')} />
          </Stack>
        );
      }
      case 'array':
        const renderCard = (defaultValue: { [key in string]: VariableValue }, index: number) => (
          <Card space={2} key={`${JSON.stringify(defaultValue)}-${index}`}>
            <Stack space={2}>
              {templates.map((template) => (
                <IntegrationField
                  key={template.name}
                  field={template}
                  allFields={allFields}
                  defaultValue={defaultValue?.[template.name]}
                  variableGroups={variableGroups}
                  uploadMutation={uploadMutation}
                  prefix={`${defaultId}${index !== undefined ? `__${index}` : ''}`}
                  defaultFiles={defaultFiles}
                  isNewAction={isNewAction}
                  fieldMappingId={fieldMappingId}
                  formStore={formStore}
                />
              ))}
              <Stack orientation="horizontal" justify="flex-end">
                <Button
                  variant="secondary"
                  iconLeft={<IconTrash />}
                  onClick={() => handleRemoveClick(index)}
                />
              </Stack>
            </Stack>
          </Card>
        );
        return (
          <Stack space={2}>
            <Text font="bold14" text={field.label} />
            {Array.isArray(defaultValue)
              ? defaultValue.map((val, index) => renderCard(val, index))
              : renderCard({}, 0)}
            <Stack orientation="horizontal" justify="flex-end">
              <Button
                text={t('addMore')}
                variant="secondary"
                iconLeft={<IconAddPlus />}
                onClick={handleAddMoreClick}
              />
            </Stack>
          </Stack>
        );
      case 'paragraph':
        return (
          <Form.FieldMappingTextEditor
            id={defaultId}
            plaintext
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
          />
        );
      case 'text':
      default:
        return (
          <Form.FieldMappingTextEditor
            id={defaultId}
            plaintext
            label={field.label}
            singleLine
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
          />
        );
    }
  };

  const Wrapper = field.visible ? Fragment : VisuallyHidden;
  return <Wrapper>{renderFieldContent()}</Wrapper>;
};
